import type {
  ProfitPlus2k8OrgConfig,
  ProfitPlus2k8ReferenceTables,
} from '../models/configuration';
import type {
  ProfitWarehouseStockRow,
  WarehouseStockMap,
} from '../models/integrations/profitPlus2k8';

/** Profit dbo.art.co_art pattern: co_lin (2) + co_subl (2) + item (3) = 7 chars. */
export function shouldUseProfitArtCodeFormula(
  ppConfig: ProfitPlus2k8OrgConfig | null | undefined
): boolean {
  if (!ppConfig?.enabled) return false;
  const st = ppConfig.syncType;
  return st === 'export_only' || st === 'bidirectional';
}

/**
 * MayorX should auto-manage `product.code` (read-only) when export to Profit is configured
 * and reference tables are available in the form (same conditions as the ERP fields card).
 */
export function shouldProfitManageProductCode(
  ppConfig: ProfitPlus2k8OrgConfig | null | undefined,
  referenceTables: unknown
): boolean {
  if (!shouldUseProfitArtCodeFormula(ppConfig)) return false;
  return referenceTables != null;
}

/**
 * True if Línea / Sublínea are inconsistent (Ítem is optional for código).
 * Invalid: only one of línea/sublínea, or ítem alone without línea+sublínea.
 */
export function profitProductCodePartialErpFields(
  fields: Record<string, string> | undefined | null
): boolean {
  const f = fields || {};
  const co = String(f.co_lin ?? '').trim();
  const sub = String(f.co_subl ?? '').trim();
  const it = String(f.item ?? '').trim();
  const any = Boolean(co || sub || it);
  const pairOk = Boolean(co && sub);
  if (!any) return false;
  if (pairOk) return false;
  return true;
}

function segment2(s: string): string {
  const t = String(s ?? '')
    .trim()
    .replace(/\s/g, '');
  if (!t) return '00';
  const d = t.replace(/\D/g, '') || t;
  if (d.length <= 2) return d.padStart(2, '0');
  return d.slice(-2);
}

function segment3Item(s: string): string {
  const t = String(s ?? '')
    .trim()
    .replace(/\D/g, '');
  if (!t) return '000';
  if (t.length <= 3) return t.padStart(3, '0');
  return t.slice(-3);
}

/**
 * Builds MayorX / Profit article code: co_lin + co_subl + item (7 characters).
 * Example: 01 + 01 + 002 → 0101002
 */
export function buildProfitArtCodeFromParts(
  co_lin: string,
  co_subl: string,
  item: string
): string {
  return `${segment2(co_lin)}${segment2(co_subl)}${segment3Item(item)}`.slice(
    0,
    7
  );
}

/**
 * True when org allows exporting products to Profit and all required ERP field codes are set
 * (`item` is optional — código uses 000 when omitted).
 * Matches ProductForm queue logic for bridge export.
 */
export function shouldRequestProductExportProfit(
  ppConfig: ProfitPlus2k8OrgConfig | null | undefined,
  fields: Record<string, string> | undefined | null
): boolean {
  if (!ppConfig?.enabled) return false;
  if (ppConfig.saveProductChangesToProfit === false) return false;
  const st = ppConfig.syncType;
  if (st !== 'export_only' && st !== 'bidirectional') return false;
  const f = fields || {};
  const required = [
    'co_lin',
    'co_cat',
    'co_subl',
    'co_color',
    'procedenci',
    'co_prov',
    'uni_venta',
  ];
  return required.every(k => String(f[k] ?? '').trim() !== '');
}

/** Normalize Firestore warehouseStock (legacy number or row object). */
export function normalizeWarehouseStockMap(
  raw: unknown
): Record<string, ProfitWarehouseStockRow> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, ProfitWarehouseStockRow> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const key = String(k ?? '').trim();
    if (!key) continue;
    if (v != null && typeof v === 'object' && !Array.isArray(v) && 'stock_act' in (v as object)) {
      const o = v as ProfitWarehouseStockRow;
      const sa = Math.max(0, Number(o.stock_act) || 0);
      const sc = Math.max(0, Number(o.stock_com) || 0);
      const sle = Math.max(0, Number(o.stock_lle) || 0);
      const sde = Math.max(0, Number(o.stock_des) || 0);
      out[key] = { stock_act: sa, stock_com: sc, stock_lle: sle, stock_des: sde };
    } else {
      const sa = Math.max(0, Number(v) || 0);
      out[key] = { stock_act: sa, stock_com: 0, stock_lle: 0, stock_des: 0 };
    }
  }
  return out;
}

/** Sum of per–sub-almacén sellable qty (stock_act − stock_com), matching global `availability` (not clamped). */
export function sumWarehouseStock(stock: WarehouseStockMap | undefined | null): number {
  if (!stock || typeof stock !== 'object') return 0;
  return Object.values(stock).reduce<number>((a, v) => {
    if (typeof v === 'number') {
      return a + (Number.isFinite(v) ? v : 0);
    }
    const sa = Number((v as ProfitWarehouseStockRow).stock_act) || 0;
    const sc = Number((v as ProfitWarehouseStockRow).stock_com) || 0;
    return a + (sa - sc);
  }, 0);
}

/** Disponible en un sub-almacén: stock_act − stock_com (not clamped). */
export function warehouseAvailableAtSub(
  stock: WarehouseStockMap | undefined | null,
  coSub: string
): number | undefined {
  const s = String(coSub ?? '').trim();
  if (!s || !stock) return undefined;
  const raw = stock[s];
  if (raw === undefined) return undefined;
  if (typeof raw === 'number') return raw;
  const sa = Number((raw as ProfitWarehouseStockRow).stock_act) || 0;
  const sc = Number((raw as ProfitWarehouseStockRow).stock_com) || 0;
  return sa - sc;
}

/** Form edits only stock_act; preserve stock_com from existing Firestore data. */
export function mergeWarehouseStockForPersistence(
  edited: Record<string, number>,
  previous: WarehouseStockMap | undefined
): WarehouseStockMap {
  const prevNorm = normalizeWarehouseStockMap(previous);
  const out: WarehouseStockMap = {};
  for (const [k, act] of Object.entries(edited)) {
    const key = String(k ?? '').trim();
    if (!key) continue;
    const sc = prevNorm[key]?.stock_com ?? 0;
    const sle = prevNorm[key]?.stock_lle ?? 0;
    const sde = prevNorm[key]?.stock_des ?? 0;
    out[key] = {
      stock_act: Math.max(0, Number(act) || 0),
      stock_com: Math.max(0, Number(sc) || 0),
      stock_lle: Math.max(0, Number(sle) || 0),
      stock_des: Math.max(0, Number(sde) || 0),
    };
  }
  return out;
}

/**
 * Total sellable (Σ (stock_act − stock_com)) from per-sub stock_act edits, preserving
 * stock_com / other fields from prior `profitPlus2k8.warehouseStock`. Aligns with
 * `product.availability` semantics; do not sum raw stock_act alone.
 */
export function sellableTotalFromWarehouseDraft(
  editedStockActBySub: Record<string, number>,
  previousWarehouseStock: WarehouseStockMap | undefined
): number {
  const merged = mergeWarehouseStockForPersistence(
    editedStockActBySub,
    previousWarehouseStock
  );
  return sumWarehouseStock(merged);
}

/**
 * Sub-almacén (dbo.sub_alma.co_sub) for default on-hand stock:
 * 1) defaultProductStockSubAlmacen when present and valid in reference data
 * 2) else first sub-almacén row whose co_alma matches orderExportAlmacen
 * 3) else first sub in reference data
 */
export function resolveDefaultProductStockSubAlmacen(
  ppConfig: ProfitPlus2k8OrgConfig | null | undefined,
  ref: ProfitPlus2k8ReferenceTables | null | undefined
): string | null {
  const subs = ref?.subAlmacenes;
  if (!subs?.length) return null;
  const explicit = (ppConfig?.defaultProductStockSubAlmacen || '').trim();
  if (explicit) {
    const exists = subs.some(s => String(s.co_sub ?? '').trim() === explicit);
    if (exists) return explicit;
  }
  const alma = (ppConfig?.orderExportAlmacen || '').trim();
  if (alma) {
    const match = subs.find(s => String(s.co_alma ?? '').trim() === alma);
    const coSub = String(match?.co_sub ?? '').trim();
    if (coSub) return coSub;
  }
  const first = String(subs[0]?.co_sub ?? '').trim();
  return first || null;
}

/** Short label for UI (almacén + sub + descripción). */
export function getDefaultProductStockSubLabel(
  ppConfig: ProfitPlus2k8OrgConfig | null | undefined,
  ref: ProfitPlus2k8ReferenceTables | null | undefined
): string {
  const coSub = resolveDefaultProductStockSubAlmacen(ppConfig, ref);
  if (!coSub || !ref?.subAlmacenes?.length) return coSub || '';
  const row = ref.subAlmacenes.find(s => String(s.co_sub ?? '').trim() === coSub);
  if (!row) return coSub;
  const alma = String(row.co_alma ?? '').trim();
  const des = String(row.des_sub ?? '').trim();
  if (alma && des) return `${alma} (${coSub}) — ${des}`;
  if (alma) return `${alma} (${coSub})`;
  return coSub;
}

/**
 * Build per–sub-almacén (co_sub) quantities for the product form.
 * If Firestore has `warehouseStock`, use it (missing subs → 0).
 * Otherwise put total `availability` on `defaultCoSub` when set (sucursal default), else first sub.
 */
/**
 * Per–sub-almacén stock_act for form inputs (editable). stock_com is preserved in profitPlus2k8 on save.
 * When there is no saved warehouse breakdown, seeds the default sub with physical stock_act
 * so that (stock_act − stock_com) matches global sellable `availability` (uses fields.stock_com when present).
 */
export function buildWarehouseStockForForm(
  profitPlus2k8:
    | { warehouseStock?: WarehouseStockMap; fields?: Record<string, unknown> }
    | undefined
    | null,
  subAlmacenes: ProfitPlus2k8ReferenceTables['subAlmacenes'],
  availability: number,
  defaultCoSub?: string | null
): Record<string, number> {
  const keys = (subAlmacenes || [])
    .map(s => String(s?.co_sub ?? '').trim())
    .filter(Boolean);
  if (!keys.length) return {};
  const raw = profitPlus2k8?.warehouseStock;
  const normalized = normalizeWarehouseStockMap(raw);
  const hasSaved = Object.keys(normalized).length > 0;
  const out: Record<string, number> = {};
  const av = Number.isFinite(Number(availability)) ? Number(availability) : 0;
  const scGlobal = Number(profitPlus2k8?.fields?.stock_com ?? 0) || 0;
  const fallbackSub =
    defaultCoSub && keys.includes(defaultCoSub) ? defaultCoSub : keys[0];
  for (const k of keys) {
    if (hasSaved) {
      if (normalized[k] !== undefined) {
        out[k] = Math.max(0, Number(normalized[k].stock_act) || 0);
      } else {
        out[k] = 0;
      }
    } else if (fallbackSub === k) {
      out[k] = av + scGlobal;
    } else {
      out[k] = 0;
    }
  }
  return out;
}
